import { useMemo, useState } from "react";
import { initialDanmaku, type DanmakuComment } from "@/mocks/gossipData";

const danmakuColors = ["#fff3d6", "#ffe0b2", "#ffd0c2", "#fffbe6", "#ffd6e8"];

const lanes = [
  { top: "8%", dur: 8.5 },
  { top: "22%", dur: 10 },
  { top: "36%", dur: 9 },
  { top: "50%", dur: 11 },
  { top: "64%", dur: 8 },
  { top: "78%", dur: 10.5 },
];

const recentComments = [
  { name: "吃瓜小能手", text: "这期也太好笑了哈哈哈" },
  { name: "前排板凳", text: "坐等反转，弹幕比视频精彩" },
  { name: "路人甲围观", text: "看懵了，这瓜一个接一个" },
  { name: "快乐源泉", text: "边看边聊真的好解压" },
];

export default function LiveRoom() {
  const [input, setInput] = useState("");
  const [danmaku, setDanmaku] = useState<DanmakuComment[]>(initialDanmaku);

  const visible = useMemo(() => danmaku.slice(-40), [danmaku]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    const color = danmakuColors[Math.floor(Math.random() * danmakuColors.length)];
    const lane = Math.floor(Math.random() * lanes.length);
    setDanmaku((prev) => [...prev, { id: Date.now(), text, color, lane }]);
    setInput("");
  };

  return (
    <section id="live" className="w-full py-12 md:py-16 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-8 md:mb-10">
          <p className="text-accent-600 font-heading text-sm mb-1">LIVE &amp; DANMAKU</p>
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">弹幕现场</h2>
          <p className="text-sm text-foreground-600">
            现在正在围观的热门现场。输入弹幕，下一秒就飘过屏幕——你就是前排观众。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* 弹幕播放器 */}
          <div className="lg:col-span-2 relative rounded-2xl overflow-hidden bg-background-950 aspect-video border border-background-800">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage:
                  "url(https://readdy.ai/api/search-image?query=energetic%20live%20entertainment%20stage%20with%20warm%20coral%20and%20amber%20spotlights%2C%20silhouettes%20of%20performer%20and%20audience%2C%20golden%20confetti%20floating%2C%20lively%20variety%20show%20atmosphere%2C%20stylized%20artistic%20photography%20with%20soft%20glow&width=1280&height=720&seq=live-stage&orientation=landscape)",
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10"></div>

            {/* 弹幕层 */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {visible.map((c) => {
                const laneCfg = lanes[c.lane % lanes.length];
                return (
                  <span
                    key={c.id}
                    className="absolute whitespace-nowrap text-sm md:text-base font-medium danmaku-text animate-danmaku-right"
                    style={{ top: laneCfg.top, color: c.color, animationDuration: `${laneCfg.dur}s` }}
                  >
                    {c.text}
                  </span>
                );
              })}
            </div>

            {/* 顶部状态 */}
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-white text-xs">
                <i className="ri-live-line text-accent-300"></i>
                围观中 · 2.4万人在线
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-white text-xs">
                <i className="ri-volume-up-line"></i>
              </span>
            </div>

            {/* 底部控制条 */}
            <div className="absolute bottom-3 left-3 right-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center"
                  aria-label="播放暂停"
                >
                  <i className="ri-pause-line"></i>
                </button>
                <button
                  type="button"
                  className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center"
                  aria-label="收藏"
                >
                  <i className="ri-heart-3-line"></i>
                </button>
                <span className="ml-auto text-white/70 text-xs">08:24</span>
              </div>
            </div>
          </div>

          {/* 弹幕输入 + 评论区 */}
          <div className="card-lift rounded-2xl p-4 md:p-5 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-foreground-950">评论区热议</h3>
              <span className="inline-flex items-center gap-1 text-xs text-primary-600 rounded-full bg-primary-50 px-2.5 py-1 whitespace-nowrap">
                <i className="ri-fire-line"></i>
                正在热议
              </span>
            </div>

            <div className="flex-1 space-y-3 mb-4">
              {recentComments.map((c, i) => (
                <div key={i} className="flex gap-2.5">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center text-xs font-medium">
                    {c.name.slice(0, 1)}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500 mb-0.5">{c.name}</p>
                    <p className="text-sm text-foreground-900">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-background-200">
              <div className="flex items-center gap-2 bg-background-100 rounded-full pl-4 pr-1 py-1">
                <i className="ri-send-plane-line text-foreground-400"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="发条弹幕凑个热闹…"
                  maxLength={40}
                  className="bg-transparent flex-1 text-sm outline-none text-foreground-900 placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={send}
                  className="rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-4 py-2 whitespace-nowrap transition-colors"
                >
                  发送
                </button>
              </div>
              <p className="text-xs text-foreground-400 mt-2">文明吃瓜，友善讨论，发弹幕也要讲礼貌哦</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}